/**
 * Which subnav child is highlighted for the current location.
 *
 * `NavLink` matches by prefix unless told otherwise, and one subnav child's
 * path is the prefix of another's: "Content Layouts" is `/layout` and "Template
 * Overrides" is `/layout/templates`. So standing on the template list lit up
 * both of them.
 *
 * `exact` is not the fix. The lists are not the only routes under those paths —
 * `/layout/:oid/edit` and `/layout/new` are real places to be — and an exact
 * match would leave the whole subnav dark as soon as you opened a layout.
 *
 * The rule is LONGEST MATCH WINS, which is what a person reading the sidebar
 * already assumes: a child owns the location when the location is inside its
 * subtree and no sibling owns a more specific subtree that also contains it.
 */

/** Trailing slashes are not part of a path's identity. */
const normalize = (path: string): string => (path || '').replace(/\/+$/, '') || '/';

/**
 * Whether `pathname` is inside `to`'s subtree.
 *
 * Segment-wise, so `/layout/templates` does not claim `/layout/templatesets`.
 * The root is exact: a link to `/` owns only `/`, or it would own everything.
 */
export const isUnderPath = (pathname: string, to: string): boolean => {
    const target = normalize(to);
    const current = normalize(pathname);
    if (target === '/') return current === '/';

    return current === target || current.startsWith(`${target}/`);
};

/**
 * Whether the child linking to `to` is the one to highlight.
 *
 * siblings - every child path in the same subnav, `to` included. Passing them
 *            is what makes this a decision between children rather than a
 *            question asked of one link in isolation.
 */
export const isActiveSubPath = (pathname: string, to: string, siblings: string[] = []): boolean => {
    if (!isUnderPath(pathname, to)) return false;

    const target = normalize(to);
    return !siblings.some((sibling) => {
        const other = normalize(sibling);
        return other.length > target.length && isUnderPath(pathname, other);
    });
};
